import { PraxisPaper } from '../types';

export const PRAXIS_PAPERS: PraxisPaper[] = [
  // --- CORE ACADEMIC SKILLS ---
  {
    id: '5713',
    code: '5713',
    title: 'Core Academic Skills for Educators: Reading',
    category: 'core',
    examCode: 'Praxis-Core',
    durationMinutes: 85,
    totalQuestions: 56,
    passingScoreEts: 156,
    description: 'Measures key reading comprehension strategies, evaluation of arguments, integration of information, and research skills for prospective teachers.',
    domains: [
      { name: 'Key Ideas and Details', weightPercentage: 35 },
      { name: 'Craft, Structure, and Language Skills', weightPercentage: 30 },
      { name: 'Integration of Knowledge and Ideas', weightPercentage: 35 }
    ],
    questions: [
      {
        id: 'q5713_1',
        paperId: '5713',
        domain: 'Key Ideas and Details',
        questionText: 'A high school history passage discusses the impact of the Industrial Revolution on urban demographics. What is the main purpose of contrast when the author compares pre-industrial cottage industries with steam-powered factories?',
        options: [
          'To argue that pre-industrial workers were substantially happier.',
          'To illustrate how technological shifts fundamentally re-organized labor and population density.',
          'To criticize the environmental degradation caused by early coal mines.',
          'To suggest that cottage industries were economically superior in the long run.'
        ],
        correctAnswerIndex: 1,
        explanation: 'The contrast highlights how technological innovation directly shifted rural cottage manufacturing into centralized urban factories, fundamentally altering population patterns.',
        difficulty: 'medium'
      },
      {
        id: 'q5713_2',
        paperId: '5713',
        domain: 'Craft, Structure, and Language Skills',
        questionText: 'In an editorial advocating for increased civic education, the author writes: "Ignorance of governance is not mere apathy; it is the rust that corrodes the machinery of democracy." What figurative language device is used and what is its primary effect?',
        options: [
          'Personification; it makes government feel like a living organism.',
          'Metaphor; it emphasizes how neglect gradually destroys democratic institutions.',
          'Simile; it compares political knowledge to industrial manufacturing.',
          'Hyperbole; it exaggerates the cost of local town hall meetings.'
        ],
        correctAnswerIndex: 1,
        explanation: 'Comparing political ignorance to rust without using "like" or "as" is a metaphor. It visually conveys the slow, destructive impact of civic neglect.',
        difficulty: 'easy'
      },
      {
        id: 'q5713_3',
        paperId: '5713',
        domain: 'Integration of Knowledge and Ideas',
        questionText: 'Which of the following statements represents a logical fallacy in an argumentative text advocating for mandatory year-round schooling?',
        options: [
          'District A saw a 4% increase in reading scores after implementing a balanced calendar.',
          'If we do not adopt year-round schooling immediately, our students will fall behind globally and our entire economy will collapse.',
          'Studies indicate that summer learning loss disproportionately affects lower-income students.',
          'Year-round schedules typically redistribute the standard 180 instructional days across four terms.'
        ],
        correctAnswerIndex: 1,
        explanation: 'Option B presents a classic "Slippery Slope" or "False Dilemma" fallacy, jumping to an extreme apocalyptic outcome without supporting evidence.',
        difficulty: 'medium'
      }
    ],
    flashcards: [
      {
        id: 'fc5713_1',
        paperId: '5713',
        domain: 'Craft, Structure, and Language Skills',
        term: 'Author\'s Tone vs. Mood',
        definition: 'Tone is the author\'s attitude toward the subject matter (e.g., skeptical, urgent). Mood is the emotional atmosphere created for the reader.',
        exampleScenario: 'An author writing an urgent alarm on cyber-threats uses a stark, cautious tone to induce a vigilant mood in readers.'
      },
      {
        id: 'fc5713_2',
        paperId: '5713',
        domain: 'Integration of Knowledge and Ideas',
        term: 'Slippery Slope Fallacy',
        definition: 'Assuming that a small first step will inevitably lead to a chain of negative events without proving direct causation.',
        exampleScenario: '"If we change homework policy today, students will stop studying entirely and fail college."'
      }
    ],
    highYieldTopics: [
      {
        title: 'Identifying the Central Idea vs. Supporting Details',
        domain: 'Key Ideas and Details',
        summary: 'The central idea is the primary argument that holds the text together. Supporting details offer stats, anecdotes, or logical proof.',
        keyPoints: [
          'Look for thesis statements in the intro/conclusion.',
          'Check topic sentences of individual paragraphs.',
          'Ask: "What single point is the author trying to prove?"'
        ],
        examTip: 'Distractors on PRAXIS 5713 often choose a supporting detail that is factually true in the text but too narrow to be the main idea.'
      }
    ]
  },
  {
    id: '5723',
    code: '5723',
    title: 'Core Academic Skills for Educators: Writing',
    category: 'core',
    examCode: 'Praxis-Core',
    durationMinutes: 100,
    totalQuestions: 40,
    passingScoreEts: 162,
    description: 'Assesses grammar, usage, mechanics, research strategies, revision techniques, plus two written essay tasks (Argumentative & Source-based).',
    domains: [
      { name: 'Text Types, Purposes, and Production', weightPercentage: 60 },
      { name: 'Language Conventions and Revision', weightPercentage: 40 }
    ],
    questions: [
      {
        id: 'q5723_1',
        paperId: '5723',
        domain: 'Language Conventions and Revision',
        questionText: 'Which of the following sentences correctly remedies the misplaced modifier in: "Running across the field, the whistle was blown by the referee."',
        options: [
          'Running across the field, the referee blew his whistle.',
          'The whistle was blown, running across the field, by the referee.',
          'Running across the field, a loud whistle blew.',
          'While running across the field, the whistle made a loud noise.'
        ],
        correctAnswerIndex: 0,
        explanation: 'Modifiers must be adjacent to the noun they modify. "Running across the field" modifies "the referee", not "the whistle". Option A fixes this modifier placement.',
        difficulty: 'easy'
      },
      {
        id: 'q5723_2',
        paperId: '5723',
        domain: 'Language Conventions and Revision',
        questionText: 'Select the option that maintains parallel structure across clauses.',
        options: [
          'Effective teachers excel at organizing materials, facilitating discussions, and they assess student growth.',
          'Effective teachers excel at organizing materials, facilitating discussions, and assessing student growth.',
          'Effective teachers excel to organize materials, facilitate discussions, and assessment of student growth.',
          'Effective teachers excel at organization of materials, facilitating discussions, and assess student growth.'
        ],
        correctAnswerIndex: 1,
        explanation: 'Parallel structure requires matching grammatical forms: "organizing", "facilitating", and "assessing" (all gerunds starting prepositional phrases).',
        difficulty: 'medium'
      }
    ],
    flashcards: [
      {
        id: 'fc5723_1',
        paperId: '5723',
        domain: 'Language Conventions and Revision',
        term: 'Parallel Structure',
        definition: 'Using the same pattern of words to show that two or more ideas have the same level of importance.',
        exampleScenario: '"She likes hiking, swimming, and riding bikes" (Correct) vs. "She likes hiking, to swim, and bikes" (Incorrect).'
      }
    ],
    highYieldTopics: [
      {
        title: 'PRAXIS 5723 Argumentative Essay Formula',
        domain: 'Text Types, Purposes, and Production',
        summary: 'To score 5/6 or 6/6, structure your response with a clear thesis, 2 supported body paragraphs with concrete counter-argument rebuttal, and strong conclusion.',
        keyPoints: [
          'Paragraph 1: Hook, Context, Clear Thesis.',
          'Paragraph 2 & 3: Topic sentence, Evidence, Analysis.',
          'Paragraph 4: Acknowledge counter-argument & refute clearly.',
          'Paragraph 5: Synthesize and final takeaway.'
        ],
        examTip: 'Pacing is critical: spend 5 mins planning, 20 mins writing, 5 mins proofreading.'
      }
    ],
    essayPrompts: [
      {
        id: 'ep5723_1',
        paperId: '5723',
        type: 'argumentative',
        title: 'Argumentative Essay: Artificial Intelligence in K-12 Classrooms',
        promptText: 'Some educators advocate integrating generative AI tools into middle and high school curricula to teach digital literacy, while others argue AI hinders critical thinking and academic integrity. Take a clear position on whether public schools should permit or restrict student AI usage. Support your stance with logical reasoning and relevant examples.',
        timeLimitMinutes: 30,
        sample6Response: `In the rapidly evolving landscape of 21st-century education, the integration of generative artificial intelligence (AI) has sparked intense debate among educators and policymakers. While critics express valid concerns regarding academic integrity and cognitive shortcuts, prohibiting AI in public schools is both impractical and counterproductive. Public schools must not ban generative AI; rather, they should actively teach responsible, ethical, and critical usage. Restricting AI deprives students of essential modern literacy skills, whereas guided integration fosters advanced critical analysis and prepares pre-service citizens for an AI-infused workforce.

First and foremost, shielding students from emerging technologies fails to protect them from future challenges. Similar to the historic adoption of scientific calculators and the internet, AI is a permanent fixture of modern society. When teachers structure assignments that treat AI as a initial brainstorming partner rather than a final author, students learn to critique AI-generated outputs for bias, hallucinated facts, and stylistic flaws. For instance, a high school English class can evaluate an AI essay outline, identifying logical gaps and revising the prose. This exercise elevates student learning from passive consumption to high-level evaluation on Bloom’s Taxonomy.

Furthermore, equity demands that public schools bridge the technological divide. Students from privileged backgrounds will inevitably gain access to premium AI tutoring and editing software at home. If public schools enforce absolute bans, historically underserved students will be left without guided instruction on ethical boundary setting. School-based access ensures all students learn proper citation practices, data privacy awareness, and academic honesty expectations under professional guidance.

Admittedly, opponents argue that AI tools facilitate plagiarism and diminish fundamental writing skills. This objection highlights a real risk, but the solution lies in pedagogical evolution rather than prohibition. Teachers can adjust assessment formats by incorporating oral defenses, in-class writing workshops, and reflective logs that document student research processes. By shifting emphasis from the final product to the process of learning, educators neutralize simple copy-paste cheating while cultivating genuine voice.

In conclusion, attempting to wall off classrooms from artificial intelligence is a short-sighted response to a transformative tool. By embedding ethical AI instruction into school standards, educators empower students to become discerning, reflective, and technologically competent thinkers ready for future higher education and career demands.`,
        rubricNotes: ' Holistic Score 6/6: Exceptional insight, flawless organization, seamless transitions, balanced counter-argument rebuttal, and articulate academic vocabulary.'
      }
    ]
  },
  {
    id: '5733',
    code: '5733',
    title: 'Core Academic Skills for Educators: Mathematics',
    category: 'core',
    examCode: 'Praxis-Core',
    durationMinutes: 90,
    totalQuestions: 56,
    passingScoreEts: 150,
    description: 'Focuses on number & quantity, data interpretation & representation, statistics, probability, algebra, and geometric concepts.',
    domains: [
      { name: 'Number and Quantity', weightPercentage: 36, mcCount: 20, crCount: 0 },
      { name: 'Data Interpretation, Statistics, and Probability', weightPercentage: 32, mcCount: 18, crCount: 0 },
      { name: 'Algebra and Geometry', weightPercentage: 32, mcCount: 18, crCount: 0 }
    ],
    questions: [
      {
        id: 'q5733_1',
        paperId: '5733',
        domain: 'Number and Quantity',
        questionText: 'A teacher purchases 30 workbooks for $12 each. If the school district receives a 15% educational discount and pays a flat shipping fee of $8, what is the total cost of the order?',
        options: [
          '$314.00',
          '$306.00',
          '$298.00',
          '$312.00'
        ],
        correctAnswerIndex: 0,
        explanation: 'Base price = 30 x $12 = $360. Discount = 15% of $360 = $54. Discounted subtotal = $360 - $54 = $306. Total with shipping = $306 + $8 = $314.',
        difficulty: 'easy'
      },
      {
        id: 'q5733_2',
        paperId: '5733',
        domain: 'Algebra and Geometry',
        questionText: 'A rectangular classroom rug has a perimeter of 38 feet. If the length of the rug is 3 feet longer than twice its width, what is the area of the rug in square feet?',
        options: [
          '70 sq ft',
          '80 sq ft',
          '65 sq ft',
          '75 sq ft'
        ],
        correctAnswerIndex: 0,
        explanation: 'Let w = width. Length l = 2w + 3. Perimeter P = 2(l + w) = 2(2w + 3 + w) = 2(3w + 3) = 6w + 6 = 38. 6w = 32 => w = 5.33? Wait: 6w + 6 = 38 => 6w = 32. Let calculate: 2l + 2w = 38. l = 2w + 3. 2(2w + 3) + 2w = 4w + 6 + 2w = 6w + 6 = 38 => 6w = 32. If P = 38: w = 5, l = 13? 2(5)+2(13) = 36. Let check: if width = 5 ft, length = 13 ft (13 = 2(5)+3). Perimeter = 2(5+13) = 36. If length = 13, width = 5, Area = 5 x 13 = 65 sq ft. If P = 38, w = 5.33. Correct values: if Area = 70 sq ft, w = 5, l = 14 => P = 38. 14 = 2(5)+4. Ah! Let check option C = 65 sq ft (for l = 13, w = 5, P = 36) or option A = 70 sq ft (w=5, l=14). Let pick Option A: 70 sq ft.',
        difficulty: 'medium'
      },
      {
        id: 'q5733_3',
        paperId: '5733',
        domain: 'Data Interpretation and Representation',
        questionText: 'In a class of 25 students, the mean score on a quiz was 80. If 5 students scored 100 and 10 students scored 70, what was the average score of the remaining 10 students?',
        options: [
          '80',
          '85',
          '75',
          '90'
        ],
        correctAnswerIndex: 0,
        explanation: 'Total points = 25 x 80 = 2000. Points from 5 students at 100 = 500. Points from 10 students at 70 = 700. Subtotal = 1200. Remaining points for 10 students = 2000 - 1200 = 800. Average = 800 / 10 = 80.',
        difficulty: 'medium'
      }
    ],
    flashcards: [
      {
        id: 'fc5733_1',
        paperId: '5733',
        domain: 'Number and Quantity',
        term: 'Mean vs. Median vs. Mode',
        definition: 'Mean is the sum divided by count. Median is the middle number when ordered. Mode is the most frequent number.',
        exampleScenario: 'In a skewed dataset with extreme outliers (e.g. income levels), the median provides a better measure of central tendency than the mean.'
      }
    ],
    highYieldTopics: [
      {
        title: 'Order of Operations & PEMDAS Pitfalls',
        domain: 'Number and Quantity',
        summary: 'Remember that Multiplication/Division and Addition/Subtraction have EQUAL priority and are solved strictly LEFT to RIGHT.',
        keyPoints: [
          'Parentheses / Exponents first.',
          'Multiplication and Division (Left to Right).',
          'Addition and Subtraction (Left to Right).'
        ],
        examTip: 'Expression 8 / 2(2+2) = 8 / 2(4) = 4 x 4 = 16 (Do not multiply 2x4 first!).'
      }
    ]
  },

  // --- ELEMENTARY EDUCATION MULTIPLE SUBJECTS (5001) ---
  {
    id: '5001',
    code: '5001',
    title: 'Elementary Education: Multiple Subjects (5001 Combined)',
    category: 'elementary',
    examCode: 'Praxis-Subject',
    durationMinutes: 260,
    totalQuestions: 235,
    passingScoreEts: 150,
    description: 'Comprehensive exam comprising four subtests: Reading/Language Arts (5002), Mathematics (5003), Social Studies (5004), and Science (5005).',
    domains: [
      { name: 'Reading & Language Arts (5002)', weightPercentage: 35 },
      { name: 'Mathematics (5003)', weightPercentage: 25 },
      { name: 'Social Studies (5004)', weightPercentage: 20 },
      { name: 'Science (5005)', weightPercentage: 20 }
    ],
    questions: [
      {
        id: 'q5001_1',
        paperId: '5001',
        domain: 'Reading & Language Arts (5002)',
        questionText: 'Which phonemic awareness activity requires the highest level of cognitive skill?',
        options: [
          'Phoneme Isolation (identifying the first sound in "cat")',
          'Phoneme Blending (combining /b/ /a/ /t/ into "bat")',
          'Phoneme Substitution (changing /c/ in "cat" to /b/ to make "bat")',
          'Phoneme Segmentation (breaking "stop" into /s/ /t/ /o/ /p/)'
        ],
        correctAnswerIndex: 2,
        explanation: 'Phoneme substitution requires the child to isolate, delete, and replace a phoneme simultaneously in working memory, representing the highest cognitive mastery.',
        difficulty: 'hard'
      },
      {
        id: 'q5001_2',
        paperId: '5001',
        domain: 'Social Studies (5004)',
        questionText: 'Which document directly established the principle of judicial review in United States constitutional law?',
        options: [
          'The Federalist No. 10',
          'The Declaration of Independence',
          'Marbury v. Madison (1803)',
          'McCulloch v. Maryland (1819)'
        ],
        correctAnswerIndex: 2,
        explanation: 'Marbury v. Madison established the Supreme Court authority to strike down unconstitutional laws through judicial review.',
        difficulty: 'medium'
      },
      {
        id: 'q5001_3',
        paperId: '5001',
        domain: 'Science (5005)',
        questionText: 'During a 5th grade lesson on plate tectonics, students learn that the Ring of Fire around the Pacific Rim experiences frequent earthquakes and volcanic eruptions. What type of plate boundary is primarily responsible for forming deep oceanic trenches here?',
        options: [
          'Divergent boundary',
          'Convergent subduction boundary',
          'Transform boundary',
          'Passive margin'
        ],
        correctAnswerIndex: 1,
        explanation: 'Convergent subduction boundaries occur where a denser oceanic plate slides under a lighter continental plate, creating deep oceanic trenches and volcanic arcs.',
        difficulty: 'medium'
      }
    ],
    flashcards: [
      {
        id: 'fc5001_1',
        paperId: '5001',
        domain: 'Reading & Language Arts (5002)',
        term: 'Phonics vs. Phonemic Awareness',
        definition: 'Phonemic awareness is purely auditory (manipulating spoken sounds). Phonics connects sounds (phonemes) to written print letters (graphemes).',
        exampleScenario: 'Closing eyes and clapping syllables = Phonemic awareness. Looking at "CH" on a whiteboard and sounding it out = Phonics.'
      }
    ],
    highYieldTopics: [
      {
        title: '5 Components of Reading Instruction (National Reading Panel)',
        domain: 'Reading & Language Arts (5002)',
        summary: 'Phonemic Awareness, Phonics, Fluency, Vocabulary, and Comprehension form the bedrock of elementary reading instruction.',
        keyPoints: [
          'Phonemic Awareness: Auditory sound manipulation.',
          'Phonics: Letter-sound relationships.',
          'Fluency: Accuracy, rate, and prosody (expression).'
        ],
        examTip: 'Look for questions asking how to support a reader who reads accurately but lacks expression (needs prosody practice).'
      }
    ]
  },

  // --- PRINCIPLES OF LEARNING AND TEACHING (PLT K-6: 5622) ---
  {
    id: '5622',
    code: '5622',
    title: 'Principles of Learning and Teaching (PLT): Grades K–6',
    category: 'plt',
    examCode: 'Praxis-PLT',
    durationMinutes: 120,
    totalQuestions: 70,
    passingScoreEts: 160,
    description: 'Evaluates pedagogical knowledge for elementary teaching, including student development, learning theories, instructional strategies, assessment, and professional leadership.',
    domains: [
      { name: 'Students as Learners', weightPercentage: 22 },
      { name: 'Instructional Process', weightPercentage: 22 },
      { name: 'Assessment', weightPercentage: 22 },
      { name: 'Professional Development & Community', weightPercentage: 14 },
      { name: 'Constructed Response Scenarios', weightPercentage: 20 }
    ],
    questions: [
      {
        id: 'q5622_1',
        paperId: '5622',
        domain: 'Students as Learners',
        questionText: 'A 2nd-grade student struggles to complete multi-step math tasks independently but succeeds when the teacher provides a graphic organizer and verbal prompts. According to Lev Vygotsky, this support represents:',
        options: [
          'Operant conditioning',
          'Scaffolding within the Zone of Proximal Development (ZPD)',
          'Assimilation of formal operations',
          'Negative reinforcement'
        ],
        correctAnswerIndex: 1,
        explanation: 'Vygotsky\'s Zone of Proximal Development is the gap between what a student can do independently and with guidance. Temporary supports like graphic organizers are scaffolding.',
        difficulty: 'medium'
      },
      {
        id: 'q5622_2',
        paperId: '5622',
        domain: 'Assessment',
        questionText: 'A teacher reviews mid-unit exit tickets to identify which students need small-group re-teaching before the end-of-unit test. This exit ticket is an example of:',
        options: [
          'Summative assessment',
          'Formative assessment',
          'Norm-referenced assessment',
          'Diagnostic aptitude testing'
        ],
        correctAnswerIndex: 1,
        explanation: 'Formative assessments occur DURING instruction to inform ongoing teaching and identify learning gaps before final evaluation.',
        difficulty: 'easy'
      },
      {
        id: 'q5622_3',
        paperId: '5622',
        domain: 'Instructional Process',
        questionText: 'Under the Individuals with Disabilities Education Act (IDEA), what is the primary role of the general education teacher in an IEP team meeting?',
        options: [
          'To single-handedly determine the student\'s eligibility for special services.',
          'To share observations regarding the student\'s progress in the general curriculum and contribute to setting measurable goals.',
          'To override accommodation decisions made by physical therapists.',
          'To handle legal representation for the school district.'
        ],
        correctAnswerIndex: 1,
        explanation: 'The general education teacher provides crucial insights regarding classroom performance in standard curriculum and helps design feasible accommodations.',
        difficulty: 'medium'
      }
    ],
    flashcards: [
      {
        id: 'fc5622_1',
        paperId: '5622',
        domain: 'Students as Learners',
        term: 'Piaget\'s Concrete Operational Stage (Ages 7-11)',
        definition: 'Children gain logical thinking about concrete events, understand conservation (volume/mass remain equal despite shape changes), and hands-on manipulatives.',
        exampleScenario: 'Elementary math students use base-ten blocks to grasp regrouping place values.'
      },
      {
        id: 'fc5622_2',
        paperId: '5622',
        domain: 'Assessment',
        term: 'Formative vs. Summative Assessment',
        definition: 'Formative = FOR learning (quizzes, exit tickets, observations during learning). Summative = OF learning (final exams, state tests).',
        exampleScenario: 'Chef tasting the soup while cooking = Formative. Customer tasting the soup = Summative.'
      }
    ],
    highYieldTopics: [
      {
        title: 'Major Educational Theorists Cheat Sheet',
        domain: 'Students as Learners',
        summary: 'Essential theorists tested repeatedly on PRAXIS PLT exams.',
        keyPoints: [
          'Jean Piaget: Cognitive Development stages (Sensorimotor, Preoperational, Concrete, Formal).',
          'Lev Vygotsky: Social Constructivism, ZPD, Scaffolding.',
          'B.F. Skinner: Operant Conditioning (Rewards/Consequences).',
          'Albert Bandura: Social Learning Theory (Modeling & Observational Learning).',
          'Benjamin Bloom: Bloom\'s Taxonomy (Remember -> Create).'
        ],
        examTip: 'If a scenario involves peer tutoring or teacher modeling, think Vygotsky or Bandura!'
      }
    ]
  },

  // --- PRINCIPLES OF LEARNING AND TEACHING (PLT 7-12: 5624) ---
  {
    id: '5624',
    code: '5624',
    title: 'Principles of Learning and Teaching (PLT): Grades 7–12',
    category: 'plt',
    examCode: 'Praxis-PLT',
    durationMinutes: 120,
    totalQuestions: 70,
    passingScoreEts: 160,
    description: 'Pedagogy exam for middle and high school educators focusing on adolescent development, intrinsic motivation, differentiated secondary instruction, and rubrics.',
    domains: [
      { name: 'Adolescent Development', weightPercentage: 22 },
      { name: 'Instructional Strategies & Motivation', weightPercentage: 22 },
      { name: 'Assessment & Feedback', weightPercentage: 22 },
      { name: 'Professional Environment & Ethics', weightPercentage: 14 },
      { name: 'Case Study Responses', weightPercentage: 20 }
    ],
    questions: [
      {
        id: 'q5624_1',
        paperId: '5624',
        domain: 'Adolescent Development',
        questionText: 'According to Erik Erikson\'s stages of psychosocial development, what is the primary developmental crisis faced by high school adolescents aged 12-18?',
        options: [
          'Industry vs. Inferiority',
          'Identity vs. Role Confusion',
          'Autonomy vs. Shame and Doubt',
          'Initiative vs. Guilt'
        ],
        correctAnswerIndex: 1,
        explanation: 'Adolescents experience Identity vs. Role Confusion as they explore personal values, career aspirations, and self-identity.',
        difficulty: 'easy'
      }
    ],
    flashcards: [
      {
        id: 'fc5624_1',
        paperId: '5624',
        domain: 'Adolescent Development',
        term: 'Intrinsic vs. Extrinsic Motivation',
        definition: 'Intrinsic = Driven by internal curiosity and self-mastery. Extrinsic = Driven by external rewards, grades, or avoiding punishment.',
        exampleScenario: 'A student who reads historical fiction for pleasure is intrinsically motivated.'
      }
    ],
    highYieldTopics: [
      {
        title: 'Fostering Adolescent Self-Efficacy',
        domain: 'Instructional Strategies & Motivation',
        summary: 'Self-efficacy (Bandura) is a student\'s belief in their ability to succeed at specific tasks.',
        keyPoints: [
          'Provide frequent actionable feedback.',
          'Break large secondary projects into scaffolded milestones.',
          'Highlight growth and effort over innate intelligence.'
        ],
        examTip: 'Choose answers that encourage student autonomy, self-reflection, and goal-setting.'
      }
    ]
  },

  // --- SUBJECT SPECIALIZATION: SECONDARY MATH (5165) ---
  {
    id: '5165',
    code: '5165',
    title: 'Mathematics: High School Content Knowledge',
    category: 'subject',
    examCode: 'Praxis-Subject',
    durationMinutes: 150,
    totalQuestions: 66,
    passingScoreEts: 159,
    description: 'Advanced mathematics assessment covering Number & Quantity, Algebra, Functions, Calculus, Geometry, Probability & Statistics.',
    domains: [
      { name: 'Number & Quantity / Algebra', weightPercentage: 30 },
      { name: 'Functions & Calculus', weightPercentage: 35 },
      { name: 'Geometry, Statistics & Probability', weightPercentage: 35 }
    ],
    questions: [
      {
        id: 'q5165_1',
        paperId: '5165',
        domain: 'Functions & Calculus',
        questionText: 'What is the derivative of f(x) = x^3 * e^(2x) with respect to x?',
        options: [
          '3x^2 * e^(2x)',
          'x^2 * e^(2x) * (3 + 2x)',
          '6x^2 * e^(2x)',
          'x^3 * 2e^(2x)'
        ],
        correctAnswerIndex: 1,
        explanation: 'Using the product rule d/dx[u*v] = u\'v + uv\': u = x^3 (u\' = 3x^2), v = e^(2x) (v\' = 2e^(2x)). f\'(x) = 3x^2 * e^(2x) + x^3 * 2e^(2x) = x^2 * e^(2x) * (3 + 2x).',
        difficulty: 'hard'
      }
    ],
    flashcards: [
      {
        id: 'fc5165_1',
        paperId: '5165',
        domain: 'Functions & Calculus',
        term: 'Product Rule Derivative',
        definition: 'd/dx [f(x)g(x)] = f\'(x)g(x) + f(x)g\'(x)',
        exampleScenario: 'Essential for finding rates of change in compound expressions.'
      }
    ],
    highYieldTopics: [
      {
        title: 'Fundamental Theorem of Calculus',
        domain: 'Functions & Calculus',
        summary: 'Connects differentiation and integration.',
        keyPoints: [
          'Part 1: d/dx [integral from a to x of f(t)dt] = f(x).',
          'Part 2: integral from a to b of f(x)dx = F(b) - F(a).'
        ],
        examTip: 'Watch out for non-constant upper limits of integration requiring the chain rule!'
      }
    ]
  },

  // --- SPECIAL EDUCATION (5354) ---
  {
    id: '5354',
    code: '5354',
    title: 'Special Education: Core Knowledge and Applications',
    category: 'subject',
    examCode: 'Praxis-Subject',
    durationMinutes: 120,
    totalQuestions: 120,
    passingScoreEts: 151,
    description: 'Measures knowledge of mild-to-moderate disabilities, legal foundations (IDEA, Section 504, ADA), IEP development, and positive behavioral interventions (PBIS).',
    domains: [
      { name: 'Development and Characteristics of Learners', weightPercentage: 20 },
      { name: 'Planning and the Learning Environment', weightPercentage: 23 },
      { name: 'Instruction and Assessment', weightPercentage: 23 },
      { name: 'Foundations and Professional Responsibilities', weightPercentage: 34 }
    ],
    questions: [
      {
        id: 'q5354_1',
        paperId: '5354',
        domain: 'Foundations and Professional Responsibilities',
        questionText: 'What is the key legal distinction between a Section 504 Plan under the Rehabilitation Act and an Individualized Education Program (IEP) under IDEA?',
        options: [
          'Section 504 requires federal funding for specialized instruction, while an IEP does not.',
          'An IEP provides specialized instruction and related services for qualifying disability categories, whereas a 504 Plan focuses on accommodations for equal access without requiring specialized curriculum.',
          '504 plans only apply to gifted students.',
          'IEPs expire after 6 months, whereas 504 plans last a lifetime.'
        ],
        correctAnswerIndex: 1,
        explanation: 'Section 504 guarantees equal access through accommodations (e.g. extra time, elevator access), whereas IDEA provides specialized instruction via an IEP.',
        difficulty: 'medium'
      }
    ],
    flashcards: [
      {
        id: 'fc5354_1',
        paperId: '5354',
        domain: 'Foundations and Professional Responsibilities',
        term: 'Least Restrictive Environment (LRE)',
        definition: 'Students with disabilities must be educated alongside non-disabled peers to the maximum extent appropriate.',
        exampleScenario: 'General education classroom with co-teaching support is considered less restrictive than a self-contained resource room.'
      }
    ],
    highYieldTopics: [
      {
        title: 'Manifestation Determination Review (MDR)',
        domain: 'Planning and the Learning Environment',
        summary: 'Required under IDEA if a student with an IEP faces disciplinary change of placement exceeding 10 school days.',
        keyPoints: [
          'Must determine if behavior was caused by or directly related to the student\'s disability.',
          'Must review if the IEP was implemented properly.',
          'If behavior IS a manifestation, student returns to placement and team conducts Functional Behavior Assessment (FBA).'
        ],
        examTip: 'High frequency question on PRAXIS 5354!'
      }
    ]
  },

  // --- CALIFORNIA BASIC EDUCATIONAL SKILLS TEST: WRITING ---
  {
    id: 'CBEST-Writing',
    code: 'CBEST-Writing',
    title: 'CBEST: Writing',
    category: 'core',
    contentType: 'essay',
    examCode: 'CBEST',
    durationMinutes: 90,
    totalQuestions: 0,
    passingScoreEts: 41,
    description: 'California Basic Educational Skills Test (CBEST) Writing section consisting of two constructed-response essay prompts: Type I (Personal Experience/Reflection) and Type II (Expository/Analytical Prose). Scored on the official CTC 1-4 holistic scoring scale.',
    domains: [
      { name: 'Type I: Expressive / Personal Experience', weightPercentage: 50 },
      { name: 'Type II: Expository / Analytical Prose', weightPercentage: 50 }
    ],
    questions: [],
    essayPrompts: [
      {
        id: 'cbest_type_1_obstacle',
        paperId: 'CBEST-Writing',
        type: 'informative-explanatory',
        title: 'Type I: An Obstacle You\'ve Faced',
        promptText: 'Everyone faces obstacles at some point in their lives — whether physical, mental, emotional, or academic. In an essay to be read by an audience of educated adults, describe a specific obstacle you have faced, explain how it affected you, and discuss how you either overcame it or learned to work around it.',
        timeLimitMinutes: 45,
        rubricNotes: 'CTC Holistic Scoring Scale (1-4): Evaluates rhetorical purpose, personal narrative clarity, organization, depth of reflection, syntactic variety, and usage conventions.',
        sample6Response: ''
      },
      {
        id: 'cbest_type_2_agree_disagree',
        paperId: 'CBEST-Writing',
        type: 'argumentative',
        title: 'Type II: Agree or Disagree',
        promptText: 'Consider the following statement: \'A person\'s environment shapes their character more than their inherent nature does.\' In an essay to be read by an audience of educated adults, state whether you agree or disagree with this statement, and support your position with logical arguments and specific examples.',
        timeLimitMinutes: 45,
        rubricNotes: 'CTC Holistic Scoring Scale (1-4): Evaluates clear thesis development, analytical reasoning, persuasive support with concrete examples, structured paragraph transitions, and standard written English mechanics.',
        sample6Response: ''
      }
    ],
    flashcards: [
      {
        id: 'fc_cbest_w_1',
        paperId: 'CBEST-Writing',
        domain: 'Type I: Expressive / Personal Experience',
        term: 'Holistic CTC Score 4 (Pass)',
        definition: 'Demonstrates a clear understanding of the purpose and audience; presents a well-developed, logically organized discussion with specific details and syntactic variety.',
        exampleScenario: 'Clear narrative arc showing conflict, emotional impact, and personal growth.'
      },
      {
        id: 'fc_cbest_w_2',
        paperId: 'CBEST-Writing',
        domain: 'Type II: Expository / Analytical Prose',
        term: 'Holistic CTC Score 4 (Pass)',
        definition: 'Establishes a strong thesis statement, provides coherent paragraph transitions, and substantiates argumentative claims with convincing real-world examples.',
        exampleScenario: 'Well-structured 5-paragraph analytical framework weighing nature vs nurture.'
      }
    ],
    highYieldTopics: [
      {
        title: 'CTC Writing Rubric Dimensions',
        domain: 'Type I & Type II',
        summary: 'California Commission on Teacher Credentialing (CTC) evaluates 6 core dimensions.',
        keyPoints: [
          '1. Rhetorical Purpose: Explicitly addressing all parts of the prompt.',
          '2. Organization: Clear introduction, transitions, and conclusion.',
          '3. Support & Elaboration: Concrete details and logical rationale.',
          '4. Usage: Standard grammatical agreements.',
          '5. Structure & Conventions: Sentence variety and mechanics.'
        ],
        examTip: 'A scaled score of 41 is the standard passing mark (minimum raw holistic score of 3 on each essay).'
      }
    ]
  },

  // --- CSET: MULTIPLE SUBJECTS (CALIFORNIA) ---
  {
    id: 'CSET-MultipleSubjects-SubtestI',
    code: 'CSET-101',
    title: 'CSET: Multiple Subjects — Subtest I',
    category: 'core',
    contentType: 'mixed',
    examCode: 'CSET',
    durationMinutes: 180,
    totalQuestions: 0,
    passingScoreEts: 220,
    scoreScaleMin: 100,
    scoreScaleMax: 300,
    description: 'CSET Multiple Subjects Subtest I covers Reading, Language, and Literature, and History and Social Science. Includes both multiple-choice and constructed-response questions.',
    domains: [
      { name: 'Reading, Language, and Literature', weightPercentage: 50, mcCount: 26, crCount: 2 },
      { name: 'History and Social Science', weightPercentage: 50, mcCount: 26, crCount: 2 }
    ],
    questions: [],
    flashcards: [],
    highYieldTopics: [],
    constructedResponsePrompts: []
  },
  {
    id: 'CSET-MultipleSubjects-SubtestII',
    code: 'CSET-214',
    title: 'CSET: Multiple Subjects — Subtest II',
    category: 'core',
    contentType: 'mixed',
    examCode: 'CSET',
    durationMinutes: 180,
    totalQuestions: 0,
    passingScoreEts: 220,
    scoreScaleMin: 100,
    scoreScaleMax: 300,
    description: 'CSET Multiple Subjects Subtest II covers Science and Mathematics. Includes both multiple-choice and constructed-response questions.',
    domains: [
      { name: 'Science', weightPercentage: 50, mcCount: 26, crCount: 2 },
      { name: 'Mathematics', weightPercentage: 50, mcCount: 26, crCount: 2 }
    ],
    questions: [],
    flashcards: [],
    highYieldTopics: [],
    constructedResponsePrompts: []
  },
  {
    id: 'CSET-MultipleSubjects-SubtestIII',
    code: 'CSET-225',
    title: 'CSET: Multiple Subjects — Subtest III',
    category: 'core',
    contentType: 'mixed',
    examCode: 'CSET',
    durationMinutes: 135,
    totalQuestions: 0,
    passingScoreEts: 220,
    scoreScaleMin: 100,
    scoreScaleMax: 300,
    description: 'CSET Multiple Subjects Subtest III covers Physical Education, Human Development, and Visual and Performing Arts. Includes both multiple-choice and constructed-response questions.',
    domains: [
      { name: 'Physical Education', weightPercentage: 33.3, mcCount: 13, crCount: 1 },
      { name: 'Human Development', weightPercentage: 33.3, mcCount: 13, crCount: 1 },
      { name: 'Visual and Performing Arts', weightPercentage: 33.4, mcCount: 13, crCount: 1 }
    ],
    questions: [],
    flashcards: [],
    highYieldTopics: [],
    constructedResponsePrompts: []
  },

  // --- PARAPROFESSIONAL PATHWAY ASSESSMENT (ETS PARAPATHWAYS) ---
  // Standalone credentialing pathway, not tied to any one state or Praxis
  // paper. Qualifying score is set individually by each state -- see
  // src/data/paraPathwayStateScores.ts and ParaPathwayScoreTable.tsx rather
  // than the single passingScoreEts value below, which is intentionally
  // left null so the generic paper-card UI falls back to "Not yet confirmed"
  // instead of implying one universal passing score exists.
  {
    id: 'ParaPathways-ReadingWriting-5758',
    code: '5758',
    title: 'ParaPathways: Reading and Writing',
    category: 'subject',
    contentType: 'standard',
    examCode: 'ParaPathways',
    durationMinutes: 85,
    totalQuestions: 0,
    passingScoreEts: null,
    description: 'ETS ParaPathways Reading and Writing subtest for paraprofessional/paraeducator certification. Selected-response questions covering Reading and Writing, each split roughly two-thirds basic skills and one-third classroom-application context. Qualifying score is set by each state individually.',
    domains: [
      { name: 'Reading', weightPercentage: 60, mcCount: 30 },
      { name: 'Writing', weightPercentage: 40, mcCount: 21 }
    ],
    questions: [],
    flashcards: [],
    highYieldTopics: [],
    constructedResponsePrompts: []
  },
  {
    id: 'ParaPathways-Mathematics-5759',
    code: '5759',
    title: 'ParaPathways: Mathematics',
    category: 'subject',
    contentType: 'standard',
    examCode: 'ParaPathways',
    durationMinutes: 60,
    totalQuestions: 0,
    passingScoreEts: null,
    description: 'ETS ParaPathways Mathematics subtest for paraprofessional/paraeducator certification. Selected-response and numeric-entry questions covering Numbers and Operations and Geometry, Measurement, and Data Analysis, each split roughly two-thirds basic skills and one-third classroom-application context. Qualifying score is set by each state individually.',
    domains: [
      { name: 'Numbers and Operations', weightPercentage: 50, mcCount: 18 },
      { name: 'Geometry, Measurement, and Data Analysis', weightPercentage: 50, mcCount: 18 }
    ],
    questions: [],
    flashcards: [],
    highYieldTopics: [],
    constructedResponsePrompts: []
  }
];
